import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Search, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ExceptionType, ExceptionSeverity, ExceptionStatus, ExceptionPriority } from '@/types/asset-audit';
import type { AuditException } from '@/types/asset-audit';
import { useState, useMemo } from 'react';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import type { DateRange } from 'react-day-picker';
import { MultiSelect } from '@/components/ui/multiselect';
import { Switch } from '@/components/ui/switch';

interface ExceptionFiltersTabProps {
  exceptions: AuditException[];
  activeQuickFilter: string | null;
  onQuickFilterChange: (filterId: string | null) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filters: {
    status: ExceptionStatus[];
    severity: ExceptionSeverity[];
    priority: ExceptionPriority[];
    type: ExceptionType[];
    assignedTo: string[];
    reportedBy: string[];
    reportedDateRange?: DateRange;
    requiresFollowUp?: boolean;
    ageMin?: number;
    ageMax?: number;
    audit?: string;
    location?: string[];
  };
  onFiltersChange: (filters: {
    status: ExceptionStatus[];
    severity: ExceptionSeverity[];
    priority: ExceptionPriority[];
    type: ExceptionType[];
    assignedTo: string[];
    reportedBy: string[];
    reportedDateRange?: DateRange;
    requiresFollowUp?: boolean;
    ageMin?: number;
    ageMax?: number;
    audit?: string;
    location?: string[];
  }) => void;
}

// Mock data for locations and audits
const LOCATIONS = [
  { value: 'HQ - London', label: 'HQ - London' },
  { value: 'Branch - Manchester', label: 'Branch - Manchester' },
  { value: 'Branch - Birmingham', label: 'Branch - Birmingham' },
  { value: 'Warehouse - Leeds', label: 'Warehouse - Leeds' },
  { value: 'Remote', label: 'Remote' }
];

export function ExceptionFiltersTab({ exceptions, searchTerm, onSearchChange, filters, onFiltersChange }: Readonly<ExceptionFiltersTabProps>) {
  // Collapsible states
  const [severityExpanded, setSeverityExpanded] = useState(true);
  const [statusExpanded, setStatusExpanded] = useState(true);
  const [priorityExpanded, setPriorityExpanded] = useState(true);
  const [typeExpanded, setTypeExpanded] = useState(true);
  const [assignedToExpanded, setAssignedToExpanded] = useState(false);
  const [dateExpanded, setDateExpanded] = useState(false);
  const [locationExpanded, setLocationExpanded] = useState(false);
  const [auditExpanded, setAuditExpanded] = useState(false);
  const [ageExpanded, setAgeExpanded] = useState(false);

  // Calculate counts for each filter option
  const typeCounts = exceptions.reduce((acc, exc) => {
    acc[exc.exceptionType] = (acc[exc.exceptionType] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const severityCounts = exceptions.reduce((acc, exc) => {
    acc[exc.severity] = (acc[exc.severity] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const statusCounts = exceptions.reduce((acc, exc) => {
    acc[exc.status] = (acc[exc.status] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const priorityCounts = exceptions.reduce((acc, exc) => {
    acc[exc.priority] = (acc[exc.priority] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const handleFilterChange = (filterKey: string, value: string, checked: boolean) => {
    const currentValues = (filters[filterKey as keyof typeof filters] as string[]) || [];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter(v => v !== value);
    
    onFiltersChange({ ...filters, [filterKey]: newValues });
  };

  const isFilterActive = (filterKey: string, value: string) => {
    const currentValues = (filters[filterKey as keyof typeof filters] as string[]) || [];
    return currentValues.includes(value);
  };

  const handleClearAll = () => {
    onFiltersChange({
      status: [],
      severity: [],
      priority: [],
      type: [],
      assignedTo: [],
      reportedBy: []
    });
    onSearchChange('');
  };

  // Date range handler
  const handleReportedDateChange = (range: DateRange | undefined) => {
    onFiltersChange({
      ...filters,
      reportedDateRange: range
    });
  };

  // Requires Follow-up toggle
  const handleRequiresFollowUpChange = (checked: boolean) => {
    onFiltersChange({
      ...filters,
      requiresFollowUp: checked ? true : undefined
    });
  };

  // Age handlers
  const handleAgeMinChange = (value: string) => {
    const numValue = value ? parseInt(value) : undefined;
    onFiltersChange({
      ...filters,
      ageMin: numValue
    });
  };

  const handleAgeMaxChange = (value: string) => {
    const numValue = value ? parseInt(value) : undefined;
    onFiltersChange({
      ...filters,
      ageMax: numValue
    });
  };

  // Audit search handler
  const handleAuditChange = (value: string) => {
    onFiltersChange({
      ...filters,
      audit: value || undefined
    });
  };

  // Location handler
  const handleLocationChange = (values: string[]) => {
    onFiltersChange({
      ...filters,
      location: values.length > 0 ? values : undefined
    });
  };

  // Assignee handler (for unassigned option)
  const handleUnassignedChange = (checked: boolean) => {
    if (checked) {
      onFiltersChange({
        ...filters,
        assignedTo: ['unassigned']
      });
    } else {
      onFiltersChange({
        ...filters,
        assignedTo: []
      });
    }
  };

  const typeOptions = [
    { value: ExceptionType.LocationMismatch, label: 'Location Mismatch' },
    { value: ExceptionType.ConditionIssue, label: 'Condition Issue' },
    { value: ExceptionType.NotFound, label: 'Not Found' },
    { value: ExceptionType.DataDiscrepancy, label: 'Data Discrepancy' },
    { value: ExceptionType.DuplicateAsset, label: 'Duplicate Asset' },
    { value: ExceptionType.ComplianceIssue, label: 'Compliance Issue' },
    { value: ExceptionType.Other, label: 'Other' },
  ];

  const severityOptions = [
    { value: ExceptionSeverity.Critical, label: 'Critical', color: 'text-red-600' },
    { value: ExceptionSeverity.High, label: 'High', color: 'text-orange-600' },
    { value: ExceptionSeverity.Medium, label: 'Medium', color: 'text-yellow-600' },
    { value: ExceptionSeverity.Low, label: 'Low', color: 'text-gray-600' },
  ];

  const statusOptions = [
    { value: ExceptionStatus.Open, label: 'Open' },
    { value: ExceptionStatus.InProgress, label: 'In Progress' },
    { value: ExceptionStatus.ActionRequired, label: 'Action Required' },
    { value: ExceptionStatus.Resolved, label: 'Resolved' },
  ];

  const priorityOptions = [
    { value: ExceptionPriority.Urgent, label: 'Urgent', color: 'text-red-600' },
    { value: ExceptionPriority.High, label: 'High', color: 'text-orange-600' },
    { value: ExceptionPriority.Medium, label: 'Medium', color: 'text-yellow-600' },
    { value: ExceptionPriority.Low, label: 'Low', color: 'text-gray-600' },
  ];

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    count += filters.status.length;
    count += filters.severity.length;
    count += filters.priority.length;
    count += filters.type.length;
    count += filters.assignedTo.length;
    count += filters.reportedBy.length;
    count += filters.reportedDateRange?.from ? 1 : 0;
    count += filters.requiresFollowUp ? 1 : 0;
    count += filters.ageMin !== undefined || filters.ageMax !== undefined ? 1 : 0;
    count += filters.audit ? 1 : 0;
    count += filters.location?.length ?? 0;
    return count;
  }, [filters]);

  return (
    <div className="flex flex-col h-full">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 px-4 pt-4 pb-3 border-b bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <Badge variant="default" className="h-5 px-2 text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </div>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="h-7 px-2 text-xs"
            >
              <RotateCcw className="mr-1.5 h-3 w-3" />
              Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Search Bar */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search exceptions..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </div>

          <Separator />

          {/* Severity Filter - CRITICAL */}
          <Collapsible open={severityExpanded} onOpenChange={setSeverityExpanded}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger className="flex items-center gap-2 hover:text-foreground transition-colors group">
                  {severityExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  )}
                  <Label className="text-sm font-semibold cursor-pointer">Severity</Label>
                  
                </CollapsibleTrigger>
                {filters.severity.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    {filters.severity.length}
                  </Badge>
                )}
              </div>

              <CollapsibleContent className="space-y-2">
                {severityOptions.map((option) => (
                  <div key={option.value} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`severity-${option.value}`}
                        checked={isFilterActive('severity', option.value)}
                        onCheckedChange={(checked) => 
                          handleFilterChange('severity', option.value, checked as boolean)
                        }
                      />
                      <label
                        htmlFor={`severity-${option.value}`}
                        className={cn(
                          "text-sm font-medium leading-none cursor-pointer",
                          option.color
                        )}
                      >
                        {option.label}
                      </label>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {severityCounts[option.value] ?? 0}
                    </Badge>
                  </div>
                ))}
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Separator />

          {/* Status Filter - CRITICAL */}
          <Collapsible open={statusExpanded} onOpenChange={setStatusExpanded}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger className="flex items-center gap-2 hover:text-foreground transition-colors group">
                  {statusExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  )}
                  <Label className="text-sm font-semibold cursor-pointer">Status</Label>
                  
                </CollapsibleTrigger>
                {filters.status.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    {filters.status.length}
                  </Badge>
                )}
              </div>

              <CollapsibleContent className="space-y-2">
                {statusOptions.map((option) => (
                  <div key={option.value} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${option.value}`}
                        checked={isFilterActive('status', option.value)}
                        onCheckedChange={(checked) => 
                          handleFilterChange('status', option.value, checked as boolean)
                        }
                      />
                      <label
                        htmlFor={`status-${option.value}`}
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {option.label}
                      </label>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {statusCounts[option.value] ?? 0}
                    </Badge>
                  </div>
                ))}
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Separator />

          {/* Exception Type Filter - CRITICAL */}
          <Collapsible open={typeExpanded} onOpenChange={setTypeExpanded}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger className="flex items-center gap-2 hover:text-foreground transition-colors group">
                  {typeExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  )}
                  <Label className="text-sm font-semibold cursor-pointer">Exception Type</Label>
                  
                </CollapsibleTrigger>
                {filters.type.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    {filters.type.length}
                  </Badge>
                )}
              </div>

              <CollapsibleContent className="space-y-2">
                {typeOptions.map((option) => (
                  <div key={option.value} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`type-${option.value}`}
                        checked={isFilterActive('type', option.value)}
                        onCheckedChange={(checked) => 
                          handleFilterChange('type', option.value, checked as boolean)
                        }
                      />
                      <label
                        htmlFor={`type-${option.value}`}
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {option.label}
                      </label>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {typeCounts[option.value] ?? 0}
                    </Badge>
                  </div>
                ))}
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Separator />

          {/* Priority Filter - HIGH */}
          <Collapsible open={priorityExpanded} onOpenChange={setPriorityExpanded}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger className="flex items-center gap-2 hover:text-foreground transition-colors group">
                  {priorityExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  )}
                  <Label className="text-sm font-semibold cursor-pointer">Priority</Label>
                  
                </CollapsibleTrigger>
                {filters.priority.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    {filters.priority.length}
                  </Badge>
                )}
              </div>

              <CollapsibleContent className="space-y-2">
                {priorityOptions.map((option) => (
                  <div key={option.value} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`priority-${option.value}`}
                        checked={isFilterActive('priority', option.value)}
                        onCheckedChange={(checked) => 
                          handleFilterChange('priority', option.value, checked as boolean)
                        }
                      />
                      <label
                        htmlFor={`priority-${option.value}`}
                        className={cn(
                          "text-sm font-medium leading-none cursor-pointer",
                          option.color
                        )}
                      >
                        {option.label}
                      </label>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {priorityCounts[option.value] ?? 0}
                    </Badge>
                  </div>
                ))}
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Separator />

          {/* Assignment Filter - HIGH */}
          <Collapsible open={assignedToExpanded} onOpenChange={setAssignedToExpanded}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger className="flex items-center gap-2 hover:text-foreground transition-colors group">
                  {assignedToExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  )}
                  <Label className="text-sm font-semibold cursor-pointer">Assigned To</Label>
                  
                </CollapsibleTrigger>
                {filters.assignedTo.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    {filters.assignedTo.length}
                  </Badge>
                )}
              </div>

              <CollapsibleContent className="space-y-4">
                {/* Unassigned Toggle */}
                <div className="flex items-center justify-between py-2 px-2 rounded-md hover:bg-muted/50 transition-colors">
                  <Label className="text-sm cursor-pointer">Unassigned Only</Label>
                  <Switch
                    checked={filters.assignedTo.includes('unassigned')}
                    onCheckedChange={handleUnassignedChange}
                  />
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Separator />

          {/* Audit Filter - HIGH */}
          <Collapsible open={auditExpanded} onOpenChange={setAuditExpanded}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger className="flex items-center gap-2 hover:text-foreground transition-colors group">
                  {auditExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  )}
                  <Label className="text-sm font-semibold cursor-pointer">Audit</Label>
                  
                </CollapsibleTrigger>
                {filters.audit && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    1
                  </Badge>
                )}
              </div>

              <CollapsibleContent className="space-y-2">
                <Input
                  type="text"
                  placeholder="Search by audit name..."
                  value={filters.audit ?? ''}
                  onChange={(e) => handleAuditChange(e.target.value)}
                  className="h-9"
                />
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Separator />

          {/* Reported Date Filter - HIGH */}
          <Collapsible open={dateExpanded} onOpenChange={setDateExpanded}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger className="flex items-center gap-2 hover:text-foreground transition-colors group">
                  {dateExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  )}
                  <Label className="text-sm font-semibold cursor-pointer">Reported Date</Label>
                  
                </CollapsibleTrigger>
                {filters.reportedDateRange?.from && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    1
                  </Badge>
                )}
              </div>

              <CollapsibleContent className="space-y-2">
                <DateRangePicker
                  dateRange={filters.reportedDateRange}
                  onDateRangeChange={handleReportedDateChange}
                  placeholder="Select date range"
                />
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Separator />

          {/* Location Filter - MEDIUM */}
          <Collapsible open={locationExpanded} onOpenChange={setLocationExpanded}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger className="flex items-center gap-2 hover:text-foreground transition-colors group">
                  {locationExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  )}
                  <Label className="text-sm font-semibold cursor-pointer">Location</Label>
                  
                </CollapsibleTrigger>
                {(filters.location?.length ?? 0) > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    {filters.location?.length}
                  </Badge>
                )}
              </div>

              <CollapsibleContent className="space-y-2">
                <MultiSelect
                  options={LOCATIONS}
                  placeholder="Select locations..."
                  value={filters.location ?? []}
                  onValueChange={handleLocationChange}
                  maxDisplay={2}
                />
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Separator />

          {/* Age/Days Open Filter - MEDIUM */}
          <Collapsible open={ageExpanded} onOpenChange={setAgeExpanded}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <CollapsibleTrigger className="flex items-center gap-2 hover:text-foreground transition-colors group">
                  {ageExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  )}
                  <Label className="text-sm font-semibold cursor-pointer">Days Open</Label>
                  
                </CollapsibleTrigger>
                {(filters.ageMin !== undefined || filters.ageMax !== undefined || filters.requiresFollowUp) && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    {(filters.ageMin !== undefined || filters.ageMax !== undefined ? 1 : 0) + (filters.requiresFollowUp ? 1 : 0)}
                  </Badge>
                )}
              </div>

              <CollapsibleContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Age Range (days)</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        type="number"
                        placeholder="Min"
                        min="0"
                        value={filters.ageMin ?? ''}
                        onChange={(e) => handleAgeMinChange(e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        type="number"
                        placeholder="Max"
                        min="0"
                        value={filters.ageMax ?? ''}
                        onChange={(e) => handleAgeMaxChange(e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </div>
                </div>

                {/* Requires Follow-up Toggle */}
                <div className="flex items-center justify-between py-2 px-2 rounded-md hover:bg-muted/50 transition-colors">
                  <Label className="text-sm cursor-pointer">Requires Follow-up</Label>
                  <Switch
                    checked={filters.requiresFollowUp ?? false}
                    onCheckedChange={handleRequiresFollowUpChange}
                  />
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>
      </ScrollArea>
    </div>
  );
}
